import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Box,
  TextField,
  Button,
  Typography,
  Link,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../context/AuthContext';
import { LoginFormData } from '../../types/auth.types';
import { authService } from '../../services/firebase/auth.service';
import EmailVerificationRequired from './EmailVerificationRequired';

// Validation schema
const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
});

interface LoginFormProps {
  onSwitchToSignup: () => void;
}

export default function LoginForm({ onSwitchToSignup }: LoginFormProps) {
  const navigate = useNavigate();
  const { login, pendingVerification, clearPendingVerification } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState<string | null>(null);

  // Forgot password state
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetEmailError, setResetEmailError] = useState('');
  const [isResetting, setIsResetting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const handleForgotPassword = async () => {
    // Validate email
    if (!resetEmail) {
      setResetEmailError('Email is required');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(resetEmail)) {
      setResetEmailError('Please enter a valid email address');
      return;
    }

    try {
      setResetEmailError('');
      setIsResetting(true);
      await authService.sendPasswordReset(resetEmail);
      enqueueSnackbar('Password reset email sent! Check your inbox.', { variant: 'success' });
      setForgotPasswordOpen(false);
      setResetEmail('');
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to send reset email';
      setResetEmailError(errorMessage);
    } finally {
      setIsResetting(false);
    }
  };

  const onSubmit = async (data: LoginFormData) => {
    try {
      setError('');
      setIsLoading(true);
      setUnverifiedEmail(data.email); // Store email for verification screen
      await login(data.email, data.password);
      // If we get here without exception, check if verification is pending
      // The pendingVerification state will be set by AuthContext
      setIsLoading(false);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to log in';
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
      setUnverifiedEmail(null);
      setIsLoading(false);
    }
  };

  // If pending verification, show the verification screen
  if (pendingVerification && unverifiedEmail) {
    return (
      <EmailVerificationRequired
        email={unverifiedEmail}
        onVerified={() => {
          clearPendingVerification();
          setUnverifiedEmail(null);
          enqueueSnackbar('Successfully logged in!', { variant: 'success' });
          navigate('/dashboard');
        }}
        onBack={() => {
          clearPendingVerification();
          setUnverifiedEmail(null);
        }}
      />
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
      <Typography variant="h5" component="h2" gutterBottom align="center">
        Sign In
      </Typography>
      <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
        Sign in to your ShiftGrid account
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TextField
        {...register('email')}
        label="Email Address"
        type="email"
        fullWidth
        margin="normal"
        autoComplete="email"
        autoFocus
        error={!!errors.email}
        helperText={errors.email?.message}
        disabled={isLoading}
      />

      <TextField
        {...register('password')}
        label="Password"
        type={showPassword ? 'text' : 'password'}
        fullWidth
        margin="normal"
        autoComplete="current-password"
        error={!!errors.password}
        helperText={errors.password?.message}
        disabled={isLoading}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={() => setShowPassword(!showPassword)}
                edge="end"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <Box sx={{ textAlign: 'right', mt: 0.5 }}>
        <Link
          component="button"
          type="button"
          variant="body2"
          onClick={() => setForgotPasswordOpen(true)}
          sx={{ cursor: 'pointer' }}
        >
          Forgot password?
        </Link>
      </Box>

      <Button
        type="submit"
        fullWidth
        variant="contained"
        size="large"
        sx={{ mt: 3, mb: 2 }}
        disabled={isLoading}
      >
        {isLoading ? <CircularProgress size={24} /> : 'Sign In'}
      </Button>

      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Don't have an account?{' '}
          <Link
            component="button"
            type="button"
            variant="body2"
            onClick={onSwitchToSignup}
            sx={{ cursor: 'pointer' }}
          >
            Sign Up
          </Link>
        </Typography>
      </Box>

      {/* Forgot Password Dialog */}
      <Dialog
        open={forgotPasswordOpen}
        onClose={() => {
          setForgotPasswordOpen(false);
          setResetEmail('');
          setResetEmailError('');
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter your email address and we'll send you a link to reset your password.
          </Typography>
          <TextField
            label="Email Address"
            type="email"
            fullWidth
            value={resetEmail}
            onChange={(e) => {
              setResetEmail(e.target.value);
              setResetEmailError('');
            }}
            error={!!resetEmailError}
            helperText={resetEmailError}
            disabled={isResetting}
            autoFocus
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setForgotPasswordOpen(false);
              setResetEmail('');
              setResetEmailError('');
            }}
            disabled={isResetting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleForgotPassword}
            disabled={isResetting}
          >
            {isResetting ? <CircularProgress size={20} /> : 'Send Reset Link'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
